import React from 'react';
import PropTypes from 'prop-types';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { partial } from '../../util/translation/translation';
import ClubheartIcon from '../../shared/components/custom/ClubheartIcon';
import AppIcon from './AppIcon';
import componentName from './componentName';

// The icon the app gives each of its own More rows and tabs (its design
// icons, svg-icons/tabIcons: BY_ICON / BY_VALUE), when the club chose none.
// Team follows the club's sport in the app; here the plain group icon.
const APP_ICON_OF = {
  news: 'newspaper',
  youTubeFeed: 'tv-minimal-play',
  videos: 'tv-minimal-play',
  video: 'tv-minimal-play',
  custom: 'external-link',
  auctions: 'gavel',
  chats: 'messages-square',
  questionaires: 'poll-bubble',
  lottery: 'raffle',
  external_store: 'store',
  sponsors: 'building-2',
  offers: 'badge-percent',
  events: 'calendar-heart',
  matchcenter: 'arena',
  externalLiveScore: 'tennis-court',
  fogisClub: 'scoreboard',
  forzasysStats: 'activity',
  players: 'users',
  stats: 'chart-column',
  tickets: 'ticket',
  shop: 'shopping-cart',
  streamsVideo: 'cast',
};
const VENDOR_ICON = {
  clubMateLottery: '/assets/icons/vendors/clubmate.svg',
  clubMateLotterySlim: '/assets/icons/vendors/clubmate.svg',
  streamsVideo: '/assets/icons/vendors/Streams.png',
};

const isDivider = (item) => !!item?.divider;

// As the app groups the menu (moreMenuGroups.js): split at the dividers,
// items before the first one an untitled list, a divider with nothing
// under it not shown
const groupMenu = (items) =>
  items
    .reduce(
      (groups, item) => {
        if (isDivider(item)) {
          groups.push({
            key: item.id,
            title: (item.divider.title || '').trim(),
            layout: item.divider.layout === 'grid' ? 'grid' : 'list',
            items: [],
          });
        } else {
          groups[groups.length - 1].items.push(item);
        }
        return groups;
      },
      [{ key: 'menu', title: '', layout: 'list', items: [] }]
    )
    .filter((group) => group.items.length > 0);

// A row's or tab's icon: the club's choice, a vendor's logo, or the app's own
export const renderPreviewIcon = (item, size, chosenIcon) => {
  if (item.id === 'goal-sponsor') {
    return <ClubheartIcon size={size} />;
  }
  const chosen = chosenIcon(item);
  if (chosen) {
    return <AppIcon name={chosen} size={size} />;
  }
  if (VENDOR_ICON[item.id]) {
    return (
      <img
        src={VENDOR_ICON[item.id]}
        alt=""
        className="phone-more__vendor"
        style={{ width: size, height: size }}
      />
    );
  }
  return <AppIcon name={APP_ICON_OF[item.id] || 'ellipsis'} size={size} />;
};

/**
 * The app's floating tab bar: Home, the club's tabs and More, with the
 * screen being shown (home or more) selected.
 */
export const PhoneTabBar = ({ tabs, selected, chosenIcon }) => {
  const p = partial('appLayout');
  const tabClass = (name) =>
    `phone-more__tab${selected === name ? ' phone-more__tab--on' : ''}`;
  return (
    <div className="phone-more__tabbar">
      <div
        className="phone-more__tabs"
        style={{
          gridTemplateColumns: `repeat(${tabs.length + 2}, minmax(0, 1fr))`,
        }}
      >
        <span className={tabClass('home')}>
          <AppIcon name="house" size={20} />
          {p('previewHome')}
        </span>
        {tabs.map((tab) => (
          <span className="phone-more__tab" key={tab.id}>
            {renderPreviewIcon(tab, 20, chosenIcon)}
            <span className="phone-more__tab-label">
              {componentName(tab.id)}
            </span>
          </span>
        ))}
        <span className={tabClass('more')}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
            <circle cx="5" cy="12" r="1.6" />
          </svg>
          {p('previewMore')}
        </span>
      </div>
    </div>
  );
};

PhoneTabBar.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.oneOf(['home', 'more']).isRequired,
  chosenIcon: PropTypes.func.isRequired,
};

PhoneTabBar.defaultProps = {
  tabs: [],
};

/**
 * The app's More screen on a phone, as fans see it with this layout: My
 * memberships first (for members), the menu in its groups as lists or
 * grids, Account last, and the floating tab bar with More selected.
 */
const MorePhonePreview = ({ menu, tabs, memberRow, clubLogo, chosenIcon }) => {
  const p = partial('appLayout');

  const renderIcon = (item, size) => renderPreviewIcon(item, size, chosenIcon);

  const groups = groupMenu(menu);

  return (
    <div className="phone-more" aria-label={p('preview')}>
      <div className="phone-more__screen">
        <div className="phone-more__content">
          {memberRow && (
            <div className="phone-more__member">
              <span className="phone-more__badge">
                {clubLogo ? <img src={clubLogo} alt="" /> : null}
              </span>
              <span className="phone-more__member-text">
                <span className="phone-more__member-name">
                  {p('memberRowName')}
                </span>
                <span className="phone-more__member-tier">
                  {p('previewTierName')}
                </span>
              </span>
              <ChevronRightIcon size={18} />
            </div>
          )}
          {groups.map((group) => (
            <div className="phone-more__group" key={group.key}>
              {group.title && (
                <span className="phone-more__eyebrow">{group.title}</span>
              )}
              {group.layout === 'grid' ? (
                <div className="phone-more__grid">
                  {group.items.map((item) => (
                    <div className="phone-more__tile" key={item.id}>
                      {renderIcon(item, 20)}
                      <span>{item.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="phone-more__list">
                  {group.items.map((item) => (
                    <div className="phone-more__row" key={item.id}>
                      {renderIcon(item, 20)}
                      <span className="phone-more__row-name">{item.name}</span>
                      <ChevronRightIcon
                        size={16}
                        className="phone-more__chevron"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="phone-more__group">
            <span className="phone-more__eyebrow">{p('previewAccount')}</span>
            <div className="phone-more__list">
              <div className="phone-more__row">
                <AppIcon name="user-round" size={20} />
                <span className="phone-more__row-name">
                  {p('previewMyPage')}
                </span>
                <ChevronRightIcon size={16} className="phone-more__chevron" />
              </div>
            </div>
          </div>
        </div>

        <PhoneTabBar tabs={tabs} selected="more" chosenIcon={chosenIcon} />
      </div>
    </div>
  );
};

MorePhonePreview.propTypes = {
  menu: PropTypes.arrayOf(PropTypes.object),
  tabs: PropTypes.arrayOf(PropTypes.object),
  memberRow: PropTypes.bool,
  clubLogo: PropTypes.string,
  chosenIcon: PropTypes.func.isRequired,
};

MorePhonePreview.defaultProps = {
  menu: [],
  tabs: [],
  memberRow: true,
  clubLogo: '',
};

export default MorePhonePreview;
